/**
 * Appointment Cost Calculation Engine
 * 
 * Single source of truth for all service cost calculations and validations.
 * Implements the specification from docs/task.md.
 * 
 * Field Semantics:
 * - cost: List price from service catalog (price_max). Set at creation, NEVER modified.
 * - cost_to_pay: Final price the client pays. This is the SOURCE OF TRUTH for billing.
 * - manual_cost: Admin override flag. When set (not null), indicates price was explicitly set.
 * - cost_per_unit: Derived: cost_to_pay / amount.
 * - first_cost: Immutable snapshot of cost_to_pay at booking creation.
 * - discount: Discount percentage (0-100). Derived but kept for convenience.
 * - amount: Quantity of service (default: 1).
 */

// ════════════════════════════════════════════════════════════════════════════
// TYPES
// ════════════════════════════════════════════════════════════════════════════

export interface ServiceCostFields {
  /** List price from service catalog (price_max). Never changes after creation. */
  cost: number;
  /** Final price the client pays. Source of truth for billing. */
  cost_to_pay: number;
  /** Admin override flag. When set, indicates price was explicitly set by admin. */
  manual_cost: number | null;
  /** Derived: cost_to_pay / amount */
  cost_per_unit: number;
  /** Immutable snapshot of cost_to_pay at booking creation. */
  first_cost: number;
  /** Discount percentage (0-100). */
  discount: number;
  /** Quantity of service (default: 1). */
  amount: number;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export type ValidationStage = 'creation' | 'processing';

// ════════════════════════════════════════════════════════════════════════════
// SERVICE CREATION FROM CATALOG
// ════════════════════════════════════════════════════════════════════════════

/**
 * Minimal catalog entry with fields we need to create a service.
 *
 * Entries come from `/company/master/{id}/services`, whose prices are already the
 * master's own (yservicestaff.price_min/price_max, else the service's) — so a line
 * created from them defaults to the chosen master's price.
 */
export interface CatalogServiceEntry {
  id: number;
  title: string;
  /** Price from catalog - typically price_min or price_max */
  price_min?: number;
  price_max?: number;
  /** Duration in seconds */
  length?: number;
}

/**
 * The unit price a catalog entry defaults a new line to: price_min, falling back
 * to price_max (a single price has min == max).
 */
export function catalogUnitPrice(entry: Pick<CatalogServiceEntry, "price_min" | "price_max">): number {
  return Number(entry.price_min ?? entry.price_max ?? 0) || 0;
}

/**
 * Full service object returned by createServiceFromCatalog.
 * This is what gets added to appointment.services.
 */
export interface NewServiceRecord extends ServiceCostFields {
  id: number;
  title: string;
  type: 'service' | 'product';
  length: number;
  quantity?: number; // Some code uses quantity alias for amount
}

/**
 * Create a new service from a catalog entry.
 * This is the SINGLE SOURCE OF TRUTH for service creation.
 * 
 * All handleAddService implementations should use this function instead
 * of manually constructing service objects.
 * 
 * Per docs/task.md Stage 1 (Creation):
 * - cost = price from catalog (price_max or price_min)
 * - cost_to_pay = cost × amount (no discount for new service)
 * - first_cost = cost_to_pay (immutable snapshot)
 * - cost_per_unit = cost_to_pay / amount
 * - manual_cost = null (not manually set)
 * - discount = 0 (no discount)
 * - amount = 1 (default quantity)
 */
export function createServiceFromCatalog(
  catalogEntry: CatalogServiceEntry,
  options?: { amount?: number; discount?: number }
): NewServiceRecord {
  // Use price_min by default, fallback to price_max
  const price = catalogUnitPrice(catalogEntry);
  const amount = safeAmount(options?.amount ?? 1);
  const discount = safePct(options?.discount ?? 0);

  // Calculate cost fields using the centralized function
  const costFields = calculateServiceCostFields({
    cost: price,
    amount,
    discount,
    manualCost: null,
    isCreation: true,
  });

  return {
    id: catalogEntry.id,
    title: catalogEntry.title,
    type: 'service',
    length: typeof catalogEntry.length === 'number' ? catalogEntry.length : 0,
    quantity: amount, // Alias for backward compatibility
    ...costFields,
  };
}


/**
 * Re-price service lines for a newly chosen master (per-master prices,
 * docs/codebase/services-staff/README.md §2.1 in the backend repo).
 *
 * `masterCatalog` is the new master's `/company/master/{id}/services` list. A line
 * moves to that master's price unless the admin set its price by hand:
 * - `manual_cost` set → unchanged (an explicit price outranks any default);
 * - a product line, or a service the new master does not offer → unchanged;
 * - otherwise `cost` = the master's price, and cost_to_pay / cost_per_unit are
 *   recomputed keeping the line's discount % and amount.
 *
 * `first_cost` is the booking-time snapshot: kept on an existing record, reset
 * only while the record is being created (`isCreation`).
 */
export interface RepriceableLine {
  id: number | string;
  type?: string;
  cost?: number | null;
  manual_cost?: number | null;
  first_cost?: number | null;
  discount?: number | null;
  amount?: number | null;
}

export function repriceServicesForMaster<T extends RepriceableLine>(
  services: T[],
  masterCatalog: Pick<CatalogServiceEntry, "id" | "price_min" | "price_max">[],
  options?: { isCreation?: boolean }
): T[] {
  const priceById = new Map<number, number>();
  for (const entry of masterCatalog ?? []) {
    priceById.set(Number(entry.id), catalogUnitPrice(entry));
  }

  return services.map((line) => {
    if (line.type === "product") return line;
    if (line.manual_cost != null) return line;
    const price = priceById.get(Number(line.id));
    if (price == null) return line;
    if (round2(price) === round2(Number(line.cost) || 0)) return line;

    const fields = calculateServiceCostFields({
      cost: price,
      amount: line.amount ?? 1,
      discount: line.discount ?? 0,
      manualCost: null,
      firstCost: line.first_cost ?? undefined,
      isCreation: options?.isCreation ?? false,
    });
    return { ...line, ...fields };
  });
}

// ════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ════════════════════════════════════════════════════════════════════════════

/**
 * Round to 2 decimal places.
 */
export function round2(n: number): number {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/**
 * Clamp a percentage value to [0, 100].
 */
export function safePct(n: any): number {
  const v = Number(n);
  if (isNaN(v) || v < 0) return 0;
  if (v > 100) return 100;
  return v;
}

/**
 * Ensure amount is at least 1.
 */
export function safeAmount(n: any): number {
  const v = Math.floor(Number(n) || 0);
  return v < 1 ? 1 : v;
}

// ════════════════════════════════════════════════════════════════════════════
// VALIDATION (per docs/task.md)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Validate service cost fields per docs/task.md rules.
 * 
 * Stage 1 (Creation) Rules:
 * - cost is required and must be positive
 * - cost_to_pay is required and must be positive
 * - Cannot provide both discount > 0 AND manual_cost
 * - discount must be 0-100
 * - manual_cost cannot be negative
 * 
 * Stage 2 (Processing) Rules:
 * - cost and cost_to_pay are optional (partial updates allowed)
 * - If provided, they must be positive
 * - Same conflict/range rules as Stage 1
 */
export function validateServiceCostFields(
  fields: Partial<ServiceCostFields>,
  stage: ValidationStage
): ValidationResult {
  const errors: string[] = [];

  // Stage 1: Required fields
  if (stage === 'creation') {
    if (fields.cost == null || fields.cost <= 0) {
      errors.push("'cost' is required and must be positive");
    }
    if (fields.cost_to_pay == null || fields.cost_to_pay <= 0) {
      errors.push("'cost_to_pay' is required and must be positive");
    }
  }

  // Stage 2: Positive when provided
  if (stage === 'processing') {
    if (fields.cost != null && fields.cost <= 0) {
      errors.push("'cost' must be positive");
    }
    if (fields.cost_to_pay != null && fields.cost_to_pay <= 0) {
      errors.push("'cost_to_pay' must be positive");
    }
  }

  // Conflict check: discount + manual_cost
  const hasDiscount = (fields.discount ?? 0) > 0;
  const hasManualCost = fields.manual_cost != null;
  if (hasDiscount && hasManualCost) {
    errors.push("Cannot provide both 'discount' and 'manual_cost'");
  }

  // Range: discount
  if (fields.discount != null) {
    if (fields.discount < 0 || fields.discount > 100) {
      errors.push("'discount' must be between 0 and 100");
    }
  }

  // Range: manual_cost
  if (fields.manual_cost != null && fields.manual_cost < 0) {
    errors.push("'manual_cost' cannot be negative");
  }

  return { valid: errors.length === 0, errors };
}

// ════════════════════════════════════════════════════════════════════════════
// CALCULATION FUNCTIONS
// ════════════════════════════════════════════════════════════════════════════

export interface CalculateServiceCostParams {
  /** Base catalog price (price_max) */
  cost: number;
  /** Quantity (default: 1) */
  amount?: number;
  /** Optional discount percentage (0-100) */
  discount?: number;
  /** Optional admin override (explicit price per unit) */
  manualCost?: number | null;
  /** Optional first_cost (for edits, preserved from initial creation) */
  firstCost?: number;
  /** Whether this is a new service being created (affects first_cost) */
  isCreation?: boolean;
}

/**
 * Calculate all service cost fields from input parameters.
 * 
 * Logic per docs/task.md:
 * - IF manual_cost is set: User typed explicit price, discount must be 0, cost_to_pay = manual_cost
 * - IF discount > 0 AND manual_cost is null: User applied discount, cost_to_pay = cost × (1 - discount/100)
 * - IF discount = 0 AND manual_cost is null: Default pricing, cost_to_pay = cost
 */
export function calculateServiceCostFields(params: CalculateServiceCostParams): ServiceCostFields {
  const cost = round2(Math.max(0, Number(params.cost) || 0));
  const amount = safeAmount(params.amount ?? 1);
  const manualCost = params.manualCost != null ? round2(Number(params.manualCost)) : null;
  let discount = safePct(params.discount ?? 0);

  let cost_to_pay: number;

  // Treat manual_cost as set only if it's not null AND not 0 (0 is not a valid price)
  const hasManualCost = manualCost != null && manualCost > 0;

  if (hasManualCost) {
    // Manual cost is set → explicit price per unit, no discount
    cost_to_pay = round2(manualCost * amount);
    discount = 0; // Clear discount when manual_cost is set
  } else if (discount > 0) {
    // Discount applied
    cost_to_pay = round2(cost * amount * (1 - discount / 100));
  } else {
    // Default: no adjustment
    cost_to_pay = round2(cost * amount);
  }

  const cost_per_unit = amount > 0 ? round2(cost_to_pay / amount) : 0;

  // first_cost: For new services, snapshot cost_to_pay. For edits, preserve existing.
  let first_cost: number;
  if (params.isCreation || params.firstCost == null) {
    first_cost = cost_to_pay;
  } else {
    first_cost = round2(Number(params.firstCost));
  }

  return {
    cost,
    cost_to_pay,
    manual_cost: hasManualCost ? manualCost : null,
    cost_per_unit,
    first_cost,
    discount,
    amount,
  };
}

/**
 * Apply a discount change to an existing service.
 * Returns a partial update to merge with the service.
 * 
 * Per docs/task.md: When discount is applied, manual_cost should be cleared.
 */
export function applyDiscountChange(
  service: Partial<ServiceCostFields>,
  newDiscount: number
): Partial<ServiceCostFields> {
  const cost = Number(service.cost) || 0;
  const amount = safeAmount(service.amount ?? 1);
  const discount = safePct(newDiscount);

  const cost_to_pay = round2(cost * amount * (1 - discount / 100));
  const cost_per_unit = amount > 0 ? round2(cost_to_pay / amount) : 0;

  return {
    discount,
    manual_cost: null, // Clear manual_cost when using discount
    cost_to_pay,
    cost_per_unit,
  };
}

/**
 * Apply a manual price change (admin sets explicit total price).
 * Returns a partial update to merge with the service.
 * 
 * Logic:
 * - If newTotalPrice > base price: It's a markup, use manual_cost
 * - If newTotalPrice <= base price: Calculate as discount if possible
 * - If no base price exists: Use manual_cost
 */
export function applyManualPriceChange(
  service: Partial<ServiceCostFields>,
  newTotalPrice: number
): Partial<ServiceCostFields> {
  const amount = safeAmount(service.amount ?? 1);
  const targetPrice = round2(Math.max(0, newTotalPrice));

  // Per docs/task.md Scenario C & D: When admin types a price directly,
  // ALWAYS use manual_cost with discount = 0, regardless of whether
  // the new price is higher or lower than base.
  const newManualUnit = amount > 0 ? round2(targetPrice / amount) : 0;

  return {
    manual_cost: newManualUnit,
    discount: 0, // Reset discount when using manual price override
    cost_to_pay: targetPrice,
    cost_per_unit: newManualUnit,
  };
}

/**
 * Apply a quantity change to an existing service.
 * Returns a partial update to merge with the service.
 * 
 * Recalculates cost_to_pay based on quantity while preserving pricing mode.
 * 
 * Per docs/task.md Scenario E: When quantity changes without a manual price override,
 * cost_to_pay = cost × amount (using base catalog price).
 * 
 * Important: This function explicitly returns manual_cost and discount in the patch
 * to ensure proper values are set when the patch is merged with the service.
 * If there's no manual override, manual_cost is explicitly set to null.
 */
export function applyQuantityChange(
  service: Partial<ServiceCostFields>,
  newQuantity: number
): Partial<ServiceCostFields> {
  const cost = Number(service.cost) || 0;
  const amount = safeAmount(newQuantity);
  const discount = safePct(service.discount ?? 0);

  // Only preserve manual_cost if it's a valid override (not null and not equal to base cost)
  // A manual_cost that equals the base cost is not a real override
  const existingManualCost = service.manual_cost != null ? Number(service.manual_cost) : null;
  const hasRealManualOverride = existingManualCost != null && existingManualCost > 0 && existingManualCost !== cost;

  let cost_to_pay: number;
  let manualCost: number | null;

  if (hasRealManualOverride) {
    // Manual cost is per-unit, multiply by new quantity
    cost_to_pay = round2(existingManualCost * amount);
    manualCost = existingManualCost;
  } else if (discount > 0) {
    // Apply discount to new total
    cost_to_pay = round2(cost * amount * (1 - discount / 100));
    manualCost = null;
  } else {
    // Default pricing - use base catalog price
    cost_to_pay = round2(cost * amount);
    manualCost = null;
  }

  const cost_per_unit = amount > 0 ? round2(cost_to_pay / amount) : 0;

  return {
    amount,
    cost_to_pay,
    cost_per_unit,
    manual_cost: manualCost,
    discount, // Preserve current discount
  };
}

// ════════════════════════════════════════════════════════════════════════════
// DETECTION HELPERS (from docs/task.md)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Returns True if price was explicitly set by admin.
 */
export function isManuallyPriced(service: Partial<ServiceCostFields>): boolean {
  return service.manual_cost != null;
}

/**
 * Returns the actual billing price.
 * Priority: manual_cost ?? cost_to_pay
 */
export function getEffectivePrice(service: Partial<ServiceCostFields>): number {
  if (service.manual_cost != null) {
    return Number(service.manual_cost);
  }
  return Number(service.cost_to_pay) || 0;
}

// ════════════════════════════════════════════════════════════════════════════
// LINE TOTAL CALCULATION (for backward compatibility with existing code)
// ════════════════════════════════════════════════════════════════════════════

/**
 * Get the base unit price for a service (before discount).
 * Handles various field formats from existing data.
 * 
 * Priority: cost → first_cost → reverse from cost_to_pay
 */
export function getServiceUnitPrice(service: any): number {
  // Standard fields first
  if (service.cost != null && service.cost !== '' && Number(service.cost) > 0) {
    return Number(service.cost);
  }
  if (service.cost_per_unit != null && service.cost_per_unit !== '') {
    return Number(service.cost_per_unit) || 0;
  }
  if (service.first_cost != null && service.first_cost !== '') {
    return Number(service.first_cost) || 0;
  }

  // Reverse-engineer from cost_to_pay if discount is known
  const qty = Math.max(1, Number(service.amount ?? service.quantity ?? 1));
  const disc = safePct(service.discount);
  if (service.cost_to_pay != null && service.cost_to_pay !== '') {
    const line = Number(service.cost_to_pay) || 0;
    const baseTotal = disc === 100 ? 0 : (line / (1 - disc / 100));
    return qty > 0 ? round2(baseTotal / qty) : 0;
  }

  return 0;
}

/**
 * Get the line total for a service (final amount to pay).
 * This is the main function that components should use to display totals.
 */
export function getServiceLineTotal(service: any): number {
  const qty = Math.max(1, Number(service.amount ?? service.quantity ?? 1));
  const disc = safePct(service.discount);

  // If cost_to_pay is set, use it directly (it's the source of truth)
  if (service.cost_to_pay != null && service.cost_to_pay !== '') {
    return round2(Number(service.cost_to_pay));
  }

  // If manual_cost is set, use it as per-unit price × qty
  if (service.manual_cost != null && service.manual_cost !== '') {
    return round2(Number(service.manual_cost) * qty);
  }

  // Calculate from unit price with discount
  const unit = getServiceUnitPrice(service);
  return round2(unit * qty * (1 - disc / 100));
}

/**
 * Calculate total for all services in an appointment.
 */
export function calculateTotalAmount(services: any[]): number {
  if (!Array.isArray(services)) return 0;
  return round2(services.reduce((sum, s) => sum + getServiceLineTotal(s), 0));
}
